import { z } from 'zod';
import { CheckinConfigResponseSchema } from './checkin-config-schemas.js';
// ── Photo Angles (JQ4-22 / US-TICKETS-002) ────────────────────────────────────
export const PHOTO_ANGLES = ['front', 'back', 'driver', 'passenger'];
export const PhotoAngleSchema = z.enum(PHOTO_ANGLES);
// ── Photo Upload URLs (JQ4-22 / US-TICKETS-002) ───────────────────────────────
export const PhotoUploadUrlsRequestSchema = z.object({});
export const PhotoUploadTargetSchema = z.object({
    angle: PhotoAngleSchema,
    url: z.string().url(),
    key: z.string().min(1),
});
export const PhotoUploadUrlsResponseSchema = z.object({
    uploads: z.array(PhotoUploadTargetSchema).length(4),
});
// ── Incident Photos (ticket-incident-photos) ──────────────────────────────────
// Optional, bounded (0..20), angle-less "incident" (pre-existing damage)
// photos captured at ticket creation. Fully independent of PHOTO_ANGLES above.
export const INCIDENT_PHOTO_CAP = 20;
export const IncidentPhotoUploadUrlsRequestSchema = z.object({
    count: z.number().int().min(1).max(INCIDENT_PHOTO_CAP),
});
export const IncidentPhotoUploadTargetSchema = z.object({
    url: z.string().url(),
    key: z.string().min(1),
});
export const IncidentPhotoUploadUrlsResponseSchema = z.object({
    uploads: z.array(IncidentPhotoUploadTargetSchema).min(1).max(INCIDENT_PHOTO_CAP),
});
// ── Ticket Status ─────────────────────────────────────────────────────────────
export const TICKET_STATUSES = [
    'parked',
    'requested',
    'in_transit',
    'delivered',
    'cancelled',
];
export const TicketStatusSchema = z.enum(TICKET_STATUSES);
// ── Visitor Type ──────────────────────────────────────────────────────────────
export const VISITOR_TYPES = ['guest', 'client', 'staff', 'other'];
export const VisitorTypeSchema = z.enum(VISITOR_TYPES);
// ── E.164 phone regex (same pattern used by LogSmsAdapter) ───────────────────
const E164 = /^\+[1-9]\d{6,14}$/;
// ── CreateTicketRequest ───────────────────────────────────────────────────────
export const CreateTicketRequestSchema = z.object({
    plate: z.string().trim().min(1).max(20),
    ticketType: z.enum(['sms', 'qr']),
    qrTag: z.string().trim().min(1).max(64).optional(),
    firstName: z.string().trim().max(60).optional(),
    lastName: z.string().trim().max(60).optional(),
    phone: z.string().regex(E164).optional(),
    visitorEmail: z.string().email().optional(),
    roomNumber: z.string().trim().max(20).optional(),
    visitorType: VisitorTypeSchema.optional(),
    parkingZone: z.string().trim().max(40).optional(),
    valetNotes: z.string().trim().max(500).optional(),
    force: z.boolean().optional(),
    /** 4 presigned-PUT storage keys — exactly 4, each non-empty (JQ4-22 / US-TICKETS-002). REQUIRED. */
    photoKeys: z.array(z.string().trim().min(1)).length(4),
    /**
     * Optional incident (pre-existing damage) photo keys — 0..20, each non-empty.
     * Fully independent of photoKeys/PHOTO_ANGLES above (ticket-incident-photos).
     * Deliberately `.optional()` WITHOUT `.default([])` — a Zod default would make
     * the inferred TS type non-optional (required-with-default), breaking every
     * existing caller that omits the field. CreateTicketUseCase already defaults
     * to [] internally (`input.incidentPhotoKeys ?? []`).
     */
    incidentPhotoKeys: z.array(z.string().trim().min(1)).max(INCIDENT_PHOTO_CAP).optional(),
    /**
     * pwa-offline-first Slice 1 (ticket-idempotent-creation) — client-generated
     * idempotency key. Optional for backward compatibility: every pre-Slice-1
     * caller omits it and gets the old (non-idempotent) behavior. When present,
     * a retry with the SAME key returns the original ticket instead of a 409
     * duplicate_active_ticket or a second ticket (spec "Idempotent ticket
     * creation by client request id").
     */
    clientRequestId: z.string().uuid().optional(),
    /**
     * pwa-offline-first Slice 5 (backend capturedAt + late-suspension
     * acceptance) — RAW device-clock check-in capture time (design D15),
     * distinct from the server-set `receivedAt`/`checkedInAt`. Optional for
     * backward compatibility: every online caller that never queues through
     * the offline outbox omits it entirely. When present, the server corrects
     * for client clock skew using the paired `X-Client-Sent-At` header (see
     * `OFFLINE_HEADERS` in offline-schemas.js) before persisting it as
     * `checkedInAt` — the RAW value is separately persisted verbatim to
     * `tickets.captured_at` for audit/idempotency purposes.
     */
    capturedAt: z.string().datetime().optional(),
});
// ── CreateTicketResponse ──────────────────────────────────────────────────────
export const TariffSnapshotSchema = z.object({
    mode: z.string(),
    rate: z.number(),
    currency: z.string(),
    // Hotel nightly rates (JQ4-17 · US-CONFIG-003).
    // Present when mode='nightly'; absent for flat/hourly/free modes.
    // Use these fields — not `rate` — to derive per-night charges.
    guestRate: z.number().optional(),
    nonGuestRate: z.number().optional(),
});
export const CreateTicketResponseSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    status: z.literal('parked'),
    plate: z.string(),
    qrTag: z.string().nullable(),
    checkedInAt: z.string(),
    smsDispatched: z.boolean(),
    tariffSnapshot: TariffSnapshotSchema.nullable().optional(),
    // frequent-customer-memberships S4 — AUTHORITATIVE coverage signal derived
    // server-side from the created ticket's membership link (coveredByMembershipId
    // != null). True ⇒ the valet must NOT charge. Exposed so the confirmation
    // screen shows "MEMBER — do not charge" independently of the advisory
    // /valet/membership/lookup endpoint (which drives the pre-submit banner but
    // may be unreachable). Optional + additive: no breaking change for existing consumers.
    coveredByMembership: z.boolean().optional(),
    // pwa-offline-first Slice 1 — true when this response is a REPLAY of an
    // original ticket (idempotent retry), not a fresh creation. Optional +
    // additive: no breaking change for existing consumers.
    replayed: z.boolean().optional(),
    /**
     * pwa-offline-first Slice 5 (product decision #18 — the FINAL,
     * SIMPLIFIED model) — `'offline_verified'` when the server accepted a
     * client capturedAt claim (against the server-observed session anchor)
     * for this ticket's `checkedInAt` WITHOUT the hard cap kicking in;
     * `'offline_capped'` when it WAS accepted but the claim was older than
     * the configured max-backdate window, so `checkedInAt` was moved forward
     * to the cap floor instead (flag for supervisor review — the raw
     * capturedAt is always recorded in the audit trail). Absent for every
     * ticket whose checkedInAt came from the server's own clock (the
     * overwhelming majority). Optional + additive.
     */
    checkinTimeSource: z.enum(['offline_verified', 'offline_capped']).optional(),
});
// ── CheckinFormConfigResponse ─────────────────────────────────────────────────
export const NicheConfigSnapshotSchema = z.object({
    niche: z.string(),
    templateVersion: z.number().optional(),
});
export const TicketTypesConfigSnapshotSchema = z.object({
    smsEnabled: z.boolean(),
    qrEnabled: z.boolean(),
    updatedAt: z.string().nullable(),
});
// ── CompsConfig snapshot (JQ4-19 slice 3) ─────────────────────────────────────
// Tells the valet PWA whether comps are enabled at the active location and
// whether the authenticated actor is authorized to apply them.
// authorizedForActor = enabled && actor.role ∈ compAuthorizedRoles (evaluated server-side).
export const CompsConfigSnapshotSchema = z.object({
    enabled: z.boolean(),
    authorizedForActor: z.boolean(),
});
export const CheckinFormConfigResponseSchema = z.object({
    niche: NicheConfigSnapshotSchema,
    checkinConfig: CheckinConfigResponseSchema.nullable(),
    ticketTypesConfig: TicketTypesConfigSnapshotSchema,
    tariffSnapshot: TariffSnapshotSchema.nullable(),
    comps: CompsConfigSnapshotSchema,
    /**
     * pwa-offline-first Slice 7 — whether the org's assigned plan includes
     * the `damageReports` feature (same gate `incident-photo-upload-urls`
     * enforces via `PlanFeatureGuard`). Lets the PWA hide incident-photo
     * capture proactively instead of learning about it reactively at sync
     * time. `.default(true)` — an OLDER cached response (persisted offline,
     * pre-Slice-7) has no such key; defaulting to `true` preserves that
     * cached response's existing behavior (incident capture shown) rather
     * than silently hiding a feature the operator could see a moment ago.
     */
    damageReportsEnabled: z.boolean().default(true),
});
// ── Retrieval Queue (JQ4-25 / US-TICKETS-005) ────────────────────────────────
export const RetrievalQueueItemSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    zone: z.string().nullable(),
    retrievalRequestedAt: z.string().nullable(),
    waitSeconds: z.number().int().nonnegative().nullable(),
    // JQ4-39 · US-PAGOS-005 — optional + additive: no breaking change for existing consumers (D6).
    paymentStatus: z.enum(['unpaid', 'paid']).optional(),
    // valet-keychain-qr — keychain code so valets can locate the keys from the queue;
    // optional + additive, null when the ticket has no keychain attached.
    qrTag: z.string().nullable().optional(),
    // QA R2-03 — set once the valet pressed "Mark as ready" (the ticket status
    // stays `in_transit`; this is the delay-recharge anchor). Optional + additive
    // so older cached responses still parse; the PWA shows a "Ready" chip from it.
    readyForPickupAt: z.string().nullable().optional(),
});
export const ClaimTicketResponseSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    status: z.literal('in_transit'),
    claimedAt: z.string(),
});
// ── DeliverTicketResponse (JQ4-26 / US-TICKETS-006) ──────────────────────────
export const DeliverTicketResponseSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    status: z.literal('delivered'),
    deliveredAt: z.string(),
    retrievalDurationSeconds: z.number().int().nonnegative().nullable(),
});
// ── MarkReadyForPickupResponse (post-payment-delay-recharge S1) ──────────────
export const MarkReadyForPickupResponseSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    readyForPickupAt: z.string(),
});
// ── Search Tickets (JQ4-23 / US-TICKETS-003) ─────────────────────────────────
export const SearchTicketsRequestSchema = z.object({
    q: z.string().trim().min(1).max(100),
});
export const TicketSearchResultItemSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable(),
    phone: z.string().nullable(),
    status: TicketStatusSchema,
    elapsedSeconds: z.number().int().nonnegative(),
    // JQ4-17 · US-CONFIG-003 — location niche for PWA exits gating (REQ-6.6).
    // 'hotel' = Exits button visible; any other niche = button hidden.
    locationNiche: z.string(),
    // JQ4-54 (JQ4-38 follow-up) — payment status so the operator can register a
    // cash payment from the search result (the only place a parked+unpaid ticket
    // is reachable). Optional + additive: no breaking change for existing consumers.
    paymentStatus: z.enum(['unpaid', 'paid']).optional(),
    // frequent-customer-memberships S4 — AUTHORITATIVE coverage signal (ticket's
    // coveredByMembershipId != null). A covered ticket is paymentStatus='paid',
    // which is otherwise indistinguishable from a cash-paid ticket; this flag lets
    // the PWA render a "MEMBER" badge so covered vehicles are recognizable in
    // Search. Optional + additive: no breaking change for existing consumers.
    coveredByMembership: z.boolean().optional(),
    // valet-keychain-qr — keychain-visibility-search: the ticket's qrTag when
    // present (null when not set — no placeholder/error, per spec). Optional +
    // nullable (mirrors paymentStatus/coveredByMembership's additive pattern
    // above): existing consumers (PWA search UI, wired in a later phase) are
    // not forced to change until they explicitly render it.
    qrTag: z.string().nullable().optional(),
});
// ── Reassign Ticket (JQ4-27 / US-TICKETS-007) ────────────────────────────────
export const ReassignTicketRequestSchema = z.object({
    /**
     * newValetId: UUID of the valet to reassign to (Escenario 1),
     * or null to return the ticket to the general retrieval queue (Escenario 4).
     */
    newValetId: z.string().uuid().nullable(),
});
export const ReassignTicketResponseSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    status: z.enum(['in_transit', 'requested']),
    retrievalValetId: z.string().uuid().nullable(),
    reassignedAt: z.string(), // ISO 8601
});
// ── Reopen Ticket (JQ4-28 / US-TICKETS-008) ──────────────────────────────────
export const ReopenTicketRequestSchema = z.object({
    /**
     * reason: supervisor must provide a non-empty reason for reopening.
     * Validated here (schema) and in the use case as a defense-in-depth.
     */
    reason: z.string().trim().min(1).max(500),
    /**
     * confirmReopenAfter24h: must be true when the ticket was closed more than
     * 24 hours ago. Absent or false triggers reopen_confirmation_required (409).
     */
    confirmReopenAfter24h: z.boolean().optional(),
});
export const ReopenTicketResponseSchema = z.object({
    id: z.string().uuid(),
    ticketNumber: z.number().int().positive(),
    plate: z.string(),
    status: z.literal('parked'),
    reopenedAt: z.string(), // ISO 8601
    previousStatus: z.enum(['delivered', 'cancelled']),
});
//# sourceMappingURL=ticket-schemas.js.map